import { RuleSeverity } from 'types/dt-graphql-apollo-v2';
import {
  aggregateRuns,
  deriveWorstStatus,
  latestRunStatus,
} from '../consistencyAggregation';

// --- Operator Constants (frontend-only, API stores as strings) ---

export enum RuleOperator {
  IN_SET = 'IN_SET',
  NOT_IN_SET = 'NOT_IN_SET',
}

export const RuleOperatorLabel: Record<RuleOperator, string> = {
  [RuleOperator.IN_SET]: 'EXISTS IN',
  [RuleOperator.NOT_IN_SET]: 'NOT EXISTS IN',
};

export enum ConditionOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  IS_ONE_OF = 'IS_ONE_OF',
  IS_NOT_ONE_OF = 'IS_NOT_ONE_OF',
  CONTAINS = 'CONTAINS',
  EXISTS = 'EXISTS',
  NOT_EXISTS = 'NOT_EXISTS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQUALS = 'GREATER_THAN_EQUALS',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQUALS = 'LESS_THAN_EQUALS',
  STARTS_WITH = 'STARTS_WITH',
  NOT_STARTS_WITH = 'NOT_STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  NOT_ENDS_WITH = 'NOT_ENDS_WITH',
  MATCHES = 'MATCHES',
  NOT_MATCHES = 'NOT_MATCHES',
  IS_UNIQUE = 'IS_UNIQUE',
}

export const ConditionOperatorLabel: Record<ConditionOperator, string> = {
  [ConditionOperator.EQUALS]: 'Equals (==)',
  [ConditionOperator.NOT_EQUALS]: 'Not equals (!=)',
  [ConditionOperator.IS_ONE_OF]: 'Is one of',
  [ConditionOperator.IS_NOT_ONE_OF]: 'Is not one of',
  [ConditionOperator.CONTAINS]: 'Contains',
  [ConditionOperator.EXISTS]: 'Exists',
  [ConditionOperator.NOT_EXISTS]: 'Not exists',
  [ConditionOperator.GREATER_THAN]: 'Greater than (>)',
  [ConditionOperator.GREATER_THAN_EQUALS]: 'Greater than or equal (>=)',
  [ConditionOperator.LESS_THAN]: 'Less than (<)',
  [ConditionOperator.LESS_THAN_EQUALS]: 'Less than or equal (<=)',
  [ConditionOperator.STARTS_WITH]: 'Starts with',
  [ConditionOperator.NOT_STARTS_WITH]: 'Not starts with',
  [ConditionOperator.ENDS_WITH]: 'Ends with',
  [ConditionOperator.NOT_ENDS_WITH]: 'Not ends with',
  [ConditionOperator.MATCHES]: 'Matches (regex)',
  [ConditionOperator.NOT_MATCHES]: 'Not matches (regex)',
  [ConditionOperator.IS_UNIQUE]: 'Is unique',
};

const VALUELESS_OPERATORS = new Set<ConditionOperator>([
  ConditionOperator.EXISTS,
  ConditionOperator.NOT_EXISTS,
  ConditionOperator.IS_UNIQUE,
]);

export const conditionOperatorNeedsValue = (
  operator: ConditionOperator
): boolean => !VALUELESS_OPERATORS.has(operator);

// --- Sort Options ---

export const sortOptions = [
  { name: 'Collection name (A-Z)', value: 'nameASC', icon: 'ArrowUp' },
  { name: 'Collection name (Z-A)', value: 'nameDESC', icon: 'ArrowDown' },
  { name: 'Last run (newest)', value: 'runTimeASC', icon: 'ArrowUp' },
  { name: 'Last run (oldest)', value: 'runTimeDESC', icon: 'ArrowDown' },
  { name: 'Created (newest)', value: 'createdASC', icon: 'ArrowUp' },
  { name: 'Created (oldest)', value: 'createdDESC', icon: 'ArrowDown' },
] as const;

export type SortOption = (typeof sortOptions)[number]['value'];

// --- View Types (what UI components consume) ---

export type ConsistencyCollectionView = {
  externalId: string;
  name: string;
  description: string | null;
  createdAt: string | null;
  labels: string[];
  reportTemplate: string | null;
  schemaFiltersCount: number;
  rulesCount: number;
  lastRun: {
    status: string;
    totalValid: number;
    totalFailed: number;
    runAt: string | number;
  } | null;
};

export type ConsistencySchemaFilterView = {
  externalId: string;
  name: string;
  description: string | null;
  schemaType: string;
  schemaCollectionId: string;
  schemaCollectionName: string;
  filters: string | null;
  labels: string[] | null;
};

export type ConditionCheckView = {
  externalId: string;
  sourceAttribute: string;
  operator: ConditionOperator;
  targetAttribute: string;
  isLiteralValue: boolean;
  useTargetSchema: boolean;
};

export type ConsistencyRuleView = {
  externalId: string;
  name: string;
  customerDefinedId: string | null;
  description: string | null;
  operator: RuleOperator;
  sourceSchemaFilter: { externalId: string; name: string };
  sourceAttribute: string;
  sourceFilters: string | null;
  targetSchemaFilter: { externalId: string; name: string };
  targetAttribute: string;
  targetFilters: string | null;
  severity: RuleSeverity;
  errorMessage: string | null;
  tags: string[] | null;
  conditionChecksOnly: boolean;
  conditionChecks: ConditionCheckView[];
};

// --- API structural types (avoids importing the huge generated file) ---

export type ApiRunStatus = {
  totalValid?: number | null;
  totalFailed?: number | null;
  status: string;
  runAt?: string | null;
  reportId?: string | null;
};

export type ApiConditionCheck = {
  externalId: string;
  sourceAttribute: string;
  operator: string;
  targetAttribute: string;
  isLiteralValue?: boolean | null;
  useTargetSchema?: boolean | null;
};

export type ApiSchemaFilter = {
  externalId: string;
  name: string;
  description?: string | null;
  schemaType: string;
  schemaCollectionId: string;
  filters?: string | null;
  labels?: (string | null)[] | null;
  projectId?: string;
};

export type ApiRule = {
  externalId: string;
  name: string;
  customerDefinedId?: string | null;
  description?: string | null;
  sourceAttribute: string;
  sourceFilters?: string | null;
  operator: string;
  targetAttribute: string;
  targetFilters?: string | null;
  severity?: string | null;
  errorMessage?: string | null;
  tags?: (string | null)[] | null;
  conditionChecksOnly?: boolean | null;
  sourceSchemaFilter?: { externalId: string; name: string } | null;
  targetSchemaFilter?: { externalId: string; name: string } | null;
  conditionChecks?: { items?: (ApiConditionCheck | null)[] | null } | null;
  runStatuses?: { items?: (ApiRunStatus | null)[] | null } | null;
};

export type ApiCollection = {
  externalId: string;
  name: string;
  description?: string | null;
  createdAt?: string | null;
  labels?: (string | null)[] | null;
  reportTemplate?: string | null;
  schemaFilters?: { items?: (ApiSchemaFilter | null)[] | null } | null;
  rules?: { items?: (ApiRule | null)[] | null } | null;
};

// --- Helpers ---

function nonNull<T>(items: (T | null)[] | null | undefined): T[] {
  return (items ?? []).filter((x): x is T => x !== null);
}

function nonNullStrings(items: (string | null)[] | null | undefined): string[] {
  return nonNull(items);
}

// --- Mapper Functions ---

export function mapCollectionToView(
  c: ApiCollection
): ConsistencyCollectionView {
  const rules = nonNull(c.rules?.items);
  const schemaFilters = nonNull(c.schemaFilters?.items);

  // Pick the latest run per rule and aggregate across them so the card shows
  // the current state of the whole collection (matching the Data Quality
  // Explorer cards and details page), not a single rule's numbers.
  const latestPerRule = rules
    .map((r) => latestRunStatus(r.runStatuses?.items))
    .filter((run): run is NonNullable<typeof run> => run !== null);

  const { statuses, totalValid, totalFailed, latestRunAt } =
    aggregateRuns(latestPerRule);

  return {
    externalId: c.externalId,
    name: c.name,
    description: c.description ?? null,
    createdAt: c.createdAt ?? null,
    labels: nonNullStrings(c.labels),
    reportTemplate: c.reportTemplate ?? null,
    schemaFiltersCount: schemaFilters.length,
    rulesCount: rules.length,
    lastRun:
      latestPerRule.length > 0
        ? {
            status: deriveWorstStatus(statuses),
            totalValid,
            totalFailed,
            runAt: latestRunAt,
          }
        : null,
  };
}

export function mapSchemaFilterToView(
  sf: ApiSchemaFilter,
  schemaNameById: Record<string, string> = {}
): ConsistencySchemaFilterView {
  return {
    externalId: sf.externalId,
    name: sf.name,
    description: sf.description ?? null,
    schemaType: sf.schemaType,
    schemaCollectionId: sf.schemaCollectionId,
    schemaCollectionName:
      schemaNameById[sf.schemaCollectionId] ?? sf.schemaCollectionId,
    filters: sf.filters ?? null,
    labels: sf.labels ? nonNullStrings(sf.labels) : null,
  };
}

export function mapRuleToView(r: ApiRule): ConsistencyRuleView {
  return {
    externalId: r.externalId,
    name: r.name,
    customerDefinedId: r.customerDefinedId ?? null,
    description: r.description ?? null,
    operator: r.operator as RuleOperator,
    sourceSchemaFilter: r.sourceSchemaFilter ?? { externalId: '', name: '' },
    sourceAttribute: r.sourceAttribute,
    sourceFilters: r.sourceFilters ?? null,
    targetSchemaFilter: r.targetSchemaFilter ?? { externalId: '', name: '' },
    targetAttribute: r.targetAttribute,
    targetFilters: r.targetFilters ?? null,
    severity: (r.severity as RuleSeverity) ?? RuleSeverity.None,
    errorMessage: r.errorMessage ?? null,
    tags: r.tags ? nonNullStrings(r.tags) : null,
    conditionChecksOnly: r.conditionChecksOnly ?? false,
    conditionChecks: nonNull(r.conditionChecks?.items).map((cc) => ({
      externalId: cc.externalId,
      sourceAttribute: cc.sourceAttribute,
      operator: cc.operator as ConditionOperator,
      targetAttribute: cc.targetAttribute,
      isLiteralValue: cc.isLiteralValue ?? false,
      useTargetSchema: cc.useTargetSchema ?? false,
    })),
  };
}
